import type { InfiniteData } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { triggerImpact } from "@/shared/lib/platform"
import { feedKeys } from "../model/keys"
import type { FeedCard, FeedList, FeedSort } from "../model/types"
import { starCard, submitDailyQuestionAnswer } from "./client"
import { cardFeedQueryOptions, dailyQuestionFeedQueryOptions } from "./queries"

/** Отправить ответ на вопрос дня → раскрыть ленту. */
export function useSubmitDailyQuestionAnswer() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (answer: string) => submitDailyQuestionAnswer(answer),
		onSuccess: () => {
			// Префикс ["feed","daily-question"] покрывает и статус, и ленту.
			queryClient.invalidateQueries({ queryKey: feedKeys.dailyQuestion() })
		},
	})
}

function isAlreadyStarred(
	data: InfiniteData<FeedList> | undefined,
	cardId: number,
): boolean {
	return !!data?.pages.some((page) =>
		page.items.some((item) => item.id === cardId && item.current_user_starred),
	)
}

function compareByStars(a: FeedCard, b: FeedCard): number {
	const starsDiff = b.count_stars - a.count_stars
	if (starsDiff !== 0) return starsDiff
	const createdDiff =
		new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
	if (createdDiff !== 0) return createdDiff
	return b.id - a.id
}

function sortPageByStars(items: FeedCard[]): FeedCard[] {
	return [...items].sort(compareByStars)
}

/** `delta: 1` — поставить звезду, `-1` — откатить. Идемпотентна: карточку,
 * уже находящуюся в целевом состоянии, не трогает. */
function patchStar(
	cardId: number,
	sort: FeedSort,
	delta: 1 | -1,
): (
	old: InfiniteData<FeedList> | undefined,
) => InfiniteData<FeedList> | undefined {
	const starred = delta > 0
	return (old) => {
		if (!old) return old
		return {
			...old,
			pages: old.pages.map((page) => {
				const items = page.items.map((item) => {
					if (item.id !== cardId || item.current_user_starred === starred) {
						return item
					}
					return {
						...item,
						count_stars: Math.max(item.count_stars + delta, 0),
						current_user_starred: starred,
					}
				})
				if (sort === "stars") return { ...page, items: sortPageByStars(items) }
				return { ...page, items }
			}),
		}
	}
}

// The open feed (typed) and every feed of its category (a prefix).
function feedQueryKeys(category: "regular" | "qotd", sort: FeedSort) {
	if (category === "qotd") {
		return {
			active: dailyQuestionFeedQueryOptions(sort).queryKey,
			category: feedKeys.dailyQuestion(),
		}
	}
	return {
		active: cardFeedQueryOptions(sort).queryKey,
		category: feedKeys.cardFeeds(),
	}
}

export function useStarFeedCard(params: {
	category: "regular" | "qotd"
	sort: FeedSort
}) {
	const queryClient = useQueryClient()
	const { active: activeQueryKey, category: categoryQueryKey } = feedQueryKeys(
		params.category,
		params.sort,
	)
	const mutationKey = feedKeys.starMutation(params.category, params.sort)

	return useMutation({
		mutationKey,
		mutationFn: (cardId: number) => starCard(cardId),
		onMutate: async (cardId: number) => {
			await queryClient.cancelQueries({ queryKey: activeQueryKey })

			const current = queryClient.getQueryData(activeQueryKey)
			const applied = !isAlreadyStarred(current, cardId)

			if (applied) {
				triggerImpact("medium")
				queryClient.setQueryData(
					activeQueryKey,
					patchStar(cardId, params.sort, 1),
				)
			}

			return { applied }
		},
		onError: (_err, cardId, context) => {
			if (context?.applied) {
				queryClient.setQueryData(
					activeQueryKey,
					patchStar(cardId, params.sort, -1),
				)
			}
		},
		onSettled: () => {
			const otherStarsInFlight = queryClient.isMutating({ mutationKey })
			if (otherStarsInFlight === 1) {
				queryClient.invalidateQueries({ queryKey: categoryQueryKey })
			}
		},
	})
}
